/*
 * Copyright (c) 2022 EGT Digital - All rights reserved.
 *
 * File: CategoryWrapper.tsx
 *
 * Written by Martin Lubenov <martin.lubenov@egt-digital.com> on  01.11.22 11:38 am.
 * Last modified: 01.11.22 11:38 am.
 *
 * More information at: https://egt-digital.com/
 */
import React, { useEffect, useRef, useState } from 'react';
import { debounceTime, fromEvent } from 'rxjs';
import { getSectionScrollPossition } from '../api';
import { shouldShowScrollers } from '../api/helpers';
import { useSettings } from '../contexts/SettingsContext';
import store from '../eventStore';
import NavScroller from './NavScroller';

const CategoryContainer = ({ children }: ICategoryContainerProps) => {
    const [settings] = useSettings();
    const [hovered, setIsHovered] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [showLeftScroller, setShowLeftScroller] = useState(false);
    const [showRightScroller, setShowRightScroller] = useState(false);
    const [scrollPossition, setScrollPosition] = useState(0);

    const name = 'navigation_bar';

    useEffect(() => {
        const { left: leftPos, top: scrollWidth } = getSectionScrollPossition(name);
        setScrollPosition(leftPos);
        if (containerRef.current && containerRef.current.scrollLeft !== leftPos) {
            const animationFrameScrollSetter = () => {
                if (scrollWidth && containerRef.current && scrollWidth > containerRef.current.scrollWidth) {
                    requestAnimationFrame(animationFrameScrollSetter);
                } else if (containerRef.current) {
                    containerRef.current.scrollLeft = leftPos;
                }
            };
            requestAnimationFrame(animationFrameScrollSetter);
        }
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            const subscription = fromEvent(containerRef.current, 'scroll')
                .pipe(debounceTime(400))
                .subscribe(e => {
                    // @ts-ignore
                    const leftScroll = e.target.scrollLeft;
                    // @ts-ignore
                    const scrollWidth = e.target.scrollWidth;
                    store.SCROLL_SECTION.next({
                        name,
                        type: 'left',
                        value: leftScroll,
                    });
                    // for the nav bar we are recording the width on the top possition
                    store.SCROLL_SECTION.next({
                        name,
                        type: 'top',
                        value: scrollWidth,
                    });
                });
            return () => subscription.unsubscribe();
        }
    }, [containerRef]);

    useEffect(() => {
        if (containerRef.current) {
            const { shouldShowLeft, shouldShowRight } = shouldShowScrollers(containerRef?.current, scrollPossition);
            setShowLeftScroller(shouldShowLeft);
            setShowRightScroller(shouldShowRight);
        }
    }, [hovered, containerRef, scrollPossition]);

    const scrollLeft = () => {
        if (containerRef && containerRef.current) {
            const scroll = containerRef.current.scrollLeft + window.innerWidth / 2;
            containerRef.current.scrollLeft = scroll;
            setScrollPosition(scroll);
        }
    };

    const scrollRight = () => {
        if (containerRef && containerRef.current) {
            const scroll = containerRef.current.scrollLeft - window.innerWidth / 2;
            containerRef.current.scrollLeft = scroll;
            setScrollPosition(scroll);
        }
    };

    return (
        <div
            ref={containerRef}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="egt_category_container"
        >
            {children}
            {hovered && showLeftScroller && settings.mode === 'desktop' && (
                <NavScroller variant="left" onClick={scrollRight} />
            )}
            {hovered && showRightScroller && settings.mode === 'desktop' && (
                <NavScroller variant="right" onClick={scrollLeft} />
            )}
        </div>
    );
};

interface ICategoryContainerProps {
    children?: React.ReactElement | any;
}

export default CategoryContainer;
